import type { HexTileMapKey } from '../hexmap/model/types';
import type { RuleSet } from '../rules';
import {
  type Building,
  type EntityId,
  fromMilli,
  type SimState,
  type Training,
  trainingNow,
  trainTime,
  type UnitType,
} from '../sim';

/**
 * A city's training queue as the city panel shows it (sim
 * systems/training.ts): per training building, the units in its slots with
 * their progress, and the ones waiting; and how many of each type are
 * queued, for the Train cards' "×N".
 */

export interface TrainingSlot {
  entry: Training;
  /** Share trained, 0–1. */
  progress: number;
  /** Whole seconds left (0 once done, waiting for room to come out). */
  secondsLeft: number;
}

export interface StoreQueue {
  /** Units training at this building now, in queue order. */
  active: TrainingSlot[];
  /** Units queued for it that wait for a slot, in queue order. */
  waiting: Training[];
}

/** `city`'s queue by training building id (only buildings with queued units). */
export function cityQueues(
  state: SimState,
  rules: RuleSet,
  city: Building,
  owners?: ReadonlyMap<HexTileMapKey, EntityId>
): Map<EntityId, StoreQueue> {
  const now = trainingNow(state, rules, city, owners);
  const out = new Map<EntityId, StoreQueue>();
  for (const entry of city.training ?? []) {
    let q = out.get(entry.at);
    if (!q) {
      q = { active: [], waiting: [] };
      out.set(entry.at, q);
    }
    if (!now.has(entry.id)) {
      q.waiting.push(entry);
      continue;
    }
    const time = trainTime(rules, entry.unitType);
    q.active.push({
      entry,
      progress: time > 0 ? Math.min(1, entry.progress / time) : 1,
      secondsLeft: Math.ceil(fromMilli(Math.max(0, time - entry.progress))),
    });
  }
  return out;
}

/** How many of each unit type `city` has queued (training or waiting). */
export function queuedCounts(city: Building): Map<UnitType, number> {
  const counts = new Map<UnitType, number>();
  for (const t of city.training ?? []) counts.set(t.unitType, (counts.get(t.unitType) ?? 0) + 1);
  return counts;
}

/** "5s" for a type that trains for 5 s; null for one that appears at once. */
export function trainTimeText(rules: RuleSet, type: UnitType): string | null {
  const seconds = rules.units[type]?.trainTime;
  return seconds === undefined ? null : `${seconds}s`;
}
